import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useAppContextNonNull } from "../../../contexts/AppContext";
import ICatalog from "../../../models/ICatalog";
import ICatalog_Item from "../../../models/ICatalog_Item";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import EndpointsEnum from "../../../models/EndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "../../../services/RestService";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwButton from "../../shared/OwButton/OwButton";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import Typeahead from "../../shared/Typeahead/Typeahead";
import IGetCatalogsDTO from "../../../models/DTO/IGetCatalogsDTO";
import IOnGetCatalogsDTO from "../../../models/DTO/IOnGetCatalogsDTO";
import ITypeaheadCatalogItemDTO from "../../../models/DTO/ITypeaheadCatalogItemDTO";
import IOnTypeaheadCatalogItemDTO from "../../../models/DTO/IOnTypeaheadCatalogItemDTO";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import "./VendorSourceAddCatalogItem.scss";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import ICatalog_Item_Vendor_Source_Map from "@models/ICatalog_Item_Vendor_Source_Map";

type Props = {
    existingCatalogItemMap?: ICatalog_Item_Vendor_Source_Map;
    vendorSourceGuid: string;
    cancelFn: () => void;
    onItemAdded: () => void;
};

export const VendorSourceAddCatalogItem: React.FC<Props> = (props) => {
    // Context
    const { socket, userModel, supplierModel, uiContext } = useAppContextNonNull();

    // State
    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [selectedCatalogGuid, setSelectedCatalogGuid] = useState<string | undefined>();
    const [costPrice, setCostPrice] = useState<number>(props.existingCatalogItemMap?.cost_price || 0);
    const [mpn, setMpn] = useState<string>(props.existingCatalogItemMap?.manuf_part_no || "");
    const [typeaheadItems, setTypeaheadItems] = useState<ICatalog_Item[]>([]);
    const [selectedTypeaheadItem, setSelectedTypeaheadItem] = useState<ICatalog_Item | undefined>();
    const [isAdding, setIsAdding] = useState(false);
    const [clearTypeahead, setClearTypeahead] = useState(false);

    // Socket: Get Catalogs
    const getCatalogs = useCallback(() => {
        const payload: IGetCatalogsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.CatalogsGetCatalogs, payload);
    }, [socket, userModel, supplierModel]);

    const onGetCatalogs = useCallback((data: IOnGetCatalogsDTO) => {
        if (props.existingCatalogItemMap) {
            for (const catalog of data.catalogs) {
                if (catalog.id === props.existingCatalogItemMap.Catalog_Item?.catalog_id) {
                    setSelectedCatalogGuid(catalog.guid);
                    setSelectedTypeaheadItem(props.existingCatalogItemMap.Catalog_Item);
                    break;
                }
            }
        }

        setCatalogs(data.catalogs);
    }, []);

    // Socket: Typeahead Catalog Items
    const typeaheadCatalogItem = (name: string) => {
        if (selectedCatalogGuid) {
            const payload: ITypeaheadCatalogItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                catalogGuid: selectedCatalogGuid,
                name,
                options: {
                    searchName: true,
                    searchDescription: true,
                    searchSku: true,
                    searchUpc: true,
                },
            };

            socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);
        }
    };

    const onTypeaheadCatalogItem = useCallback((data: IOnTypeaheadCatalogItemDTO) => {
        setTypeaheadItems(data.catalog_items);
    }, []);

    const selectTypeaheadItem = (item: ICatalog_Item) => {
        setSelectedTypeaheadItem(item);
    };

    // Form handlers
    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        switch (name) {
            case "selectedCatalogGuid":
                setSelectedCatalogGuid(value);
                setSelectedTypeaheadItem(undefined);
                setTypeaheadItems([]);
                break;
            case "cost_price":
                setCostPrice(Number(value));
                break;
            case "mpn":
                setMpn(value);
                break;
        }
    };

    const updateCatalogItem = async () => {
        if (!props.existingCatalogItemMap || !props.existingCatalogItemMap.Catalog_Item) {
            return;
        }

        setIsAdding(true);

        try {
            const url = buildRestUrl({
                endpoint: RestEndpointsEnum.UpdateVendorSourceCatalogItem,
                params: { vendorSourceGuid: props.vendorSourceGuid, catalogItemGuid: props.existingCatalogItemMap.Catalog_Item.guid },
            });

            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

            const response = await fetch(url, {
                method: "PUT",
                headers,
                body: JSON.stringify({
                    costPrice: costPrice,
                    manufPartNo: mpn,
                }),
            });

            const data = await response.json();

            if (data.success) {
                props.onItemAdded();

                // Success notification
                uiContext.notifyFn(null, {
                    message: "catalog_item_updated_successfully",
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                });

                props.cancelFn();
            }
        } catch (error) {
            console.error("Failed to update catalog item:", error);
            uiContext.notifyFn(null, {
                message: "Failed to update catalog item",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
        } finally {
            setIsAdding(false);
        }
    };

    // Add catalog item (REST API)
    const addCatalogItem = async () => {
        if (!selectedTypeaheadItem) {
            return;
        }

        setIsAdding(true);

        try {
            const url = buildRestUrl({
                endpoint: RestEndpointsEnum.CreateVendorSourceCatalogItem,
                params: { vendorSourceGuid: props.vendorSourceGuid },
            });

            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

            const response = await fetch(url, {
                method: "POST",
                headers,
                body: JSON.stringify({
                    catalogItemGuid: selectedTypeaheadItem.guid,
                    costPrice: costPrice,
                    manufPartNo: mpn,
                }),
            });

            const data = await response.json();

            if (data.success) {
                // Clear form
                setSelectedTypeaheadItem(undefined);
                setCostPrice(0);
                setMpn("");
                setTypeaheadItems([]);
                setClearTypeahead(true);

                // Notify parent to refresh list
                props.onItemAdded();

                // Success notification
                uiContext.notifyFn(null, {
                    message: "catalog_item_added_successfully",
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                });

                props.cancelFn();
            } else {
                uiContext.notifyFn(null, {
                    message: data.message || "Failed to add catalog item",
                    level: NotificationLevelEnum.error,
                    position: NotificationPositionEnum.bc,
                });
            }
        } catch (error) {
            console.error("Failed to add catalog item:", error);
            uiContext.notifyFn(null, {
                message: "Failed to add catalog item",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
        } finally {
            setIsAdding(false);
        }
    };

    const clearTypeaheadFn = () => {
        setClearTypeahead(false);
        setTypeaheadItems([]);
        setSelectedTypeaheadItem(undefined);
    };

    // Effects: Socket listeners
    useEffect(() => {
        socket.on(EndpointsEnum.CatalogsOnGetCatalogs, onGetCatalogs);
        socket.on(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onTypeaheadCatalogItem);

        return () => {
            socket.off(EndpointsEnum.CatalogsOnGetCatalogs, onGetCatalogs);
            socket.off(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onTypeaheadCatalogItem);
        };
    }, [socket, onGetCatalogs, onTypeaheadCatalogItem]);

    // Effect: Fetch catalogs on mount
    useEffect(() => {
        getCatalogs();
    }, [getCatalogs]);

    return (
        <div className="VendorSourceAddCatalogItem p-2">
            <div className="text-end">
                <OwButton centerIconClass="far fa-times" fn={props.cancelFn} className="btn-danger" />
            </div>

            <OwTitle title={props.existingCatalogItemMap ? "edit_catalog_item" : "add_catalog_item"} tag="h4" />
            <div className="d-flex flex-column">
                <div>
                    <OwLabel title={"catalog"} />
                    <OwSelectOption
                        fn={handleChange}
                        options={catalogs}
                        titleProp={"name"}
                        valueProp={"guid"}
                        name={"selectedCatalogGuid"}
                        selectedOption={selectedCatalogGuid}
                        hasSelectedOptionProp={false}
                    />
                </div>

                <div>
                    <OwLabel title={"item"} />
                    <Typeahead
                        isSelectableFn={() => true}
                        typeaheadApiFn={typeaheadCatalogItem}
                        typeaheadItems={typeaheadItems}
                        titleProp={"name"}
                        subTitleProp={"sku"}
                        selectedItemFn={selectTypeaheadItem}
                        selectedItem={selectedTypeaheadItem}
                        selectedItemTitleProp={"name"}
                        scrollableList={true}
                        clearTypeahead={clearTypeahead}
                        clearTypeaheadCallback={clearTypeaheadFn}
                    />
                </div>

                <div className="w-15r">
                    <OwLabel title={"cost_price"} />
                    <OwFnInput fn={handleChange} name={"cost_price"} value={costPrice} type="number" />
                </div>

                <div className="w-15r">
                    <OwLabel title={"manuf_part_no"} />
                    <OwFnInput fn={handleChange} name={"mpn"} value={mpn} />
                </div>

                <div className="mt-2">
                    <OwButton
                        title={props.existingCatalogItemMap ? "save" : "add"}
                        fn={props.existingCatalogItemMap ? updateCatalogItem : addCatalogItem}
                        disabled={!selectedTypeaheadItem || isAdding}
                        className={isAdding ? "btn-secondary" : "btn-primary"}
                    />
                </div>
            </div>
        </div>
    );
};
